import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { UserCheck, LogIn, LogOut, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { canAccessCentroDiurno } from "../../utils/centroDiurnoAccess";
import { getPresenze, checkIn, checkOut, getCarnetAlerts, getPrenotazioni, getCarnetOspiti } from "../../utils/centroDiurnoApi";
import PageHeader from "../../components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CentroDiurnoPresenze() {
  const { token, user, hasPermission, activeStruttura } = useAuth();
  const [dataSel, setDataSel] = useState(format(new Date(), "yyyy-MM-dd"));
  const [presenze, setPresenze] = useState([]);
  const [prenotazioni, setPrenotazioni] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [carnetMap, setCarnetMap] = useState({});
  const canManage = hasPermission("centro_diurno:manage");

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [pres, pren, al, carnetRes] = await Promise.all([
        getPresenze(token, dataSel),
        getPrenotazioni(token, { data: dataSel }),
        getCarnetAlerts(token),
        getCarnetOspiti(token),
      ]);
      setPresenze(pres.presenze || []);
      setPrenotazioni(pren.prenotazioni || []);
      setAlerts(al.alerts || []);
      const map = {};
      (carnetRes.carnet || []).forEach((c) => {
        map[c.fascicolo_id] = c;
      });
      setCarnetMap(map);
    } catch (e) {
      toast.error("Errore caricamento presenze");
    }
  }, [token, dataSel, activeStruttura?.id]);

  useEffect(() => {
    if (canAccessCentroDiurno(user)) load();
  }, [load, user]);

  const handleCheckIn = async (fascicoloId, tipoGiornata = "intera") => {
    try {
      await checkIn(token, { fascicolo_id: fascicoloId, data: dataSel, tipo_giornata: tipoGiornata });
      toast.success("Check-in registrato");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore check-in");
    }
  };

  const handleCheckOut = async (presenzaId) => {
    try {
      await checkOut(token, presenzaId);
      toast.success("Check-out registrato");
      load();
    } catch (e) {
      toast.error("Errore check-out");
    }
  };


  if (!canAccessCentroDiurno(user)) {
    return <div className="p-8 text-center text-slate-600">Modulo non attivo</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
        <PageHeader
          title="Presenze giornaliere"
          subtitle="Check-in/out digitale, carnet e servizi inclusi"
          icon={UserCheck}
          iconContainerClassName="bg-gradient-to-br from-emerald-500 to-emerald-700"
          iconClassName="text-white"
        />

        <Input type="date" value={dataSel} onChange={(e) => setDataSel(e.target.value)} className="w-auto bg-white" />

        {alerts.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-4 h-4" /> Alert Carnet (scadenza 60 giorni)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {alerts.map((a) => (
                <p key={a.fascicolo_id} className="text-sm text-amber-900">
                  {a.ospite_nome}: {a.carnet_residui} ingressi residui, scade il {a.carnet_scadenza}
                </p>
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Ospiti prenotati — {dataSel}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {prenotazioni.filter((p) => p.stato !== "annullata").length === 0 ? (
              <p className="text-sm text-slate-500">Nessuna prenotazione oggi</p>
            ) : (
              prenotazioni
                .filter((p) => p.stato !== "annullata")
                .map((p) => {
                  const pres = presenze.find((x) => x.fascicolo_id === p.fascicolo_id);
                  const haCheckIn = Boolean(pres?.check_in);
                  const haCheckOut = Boolean(pres?.check_out);
                  const carnet = carnetMap[p.fascicolo_id];
                  return (
                    <div key={p.id} className="p-4 rounded-xl border border-slate-200 bg-white flex flex-wrap gap-4 justify-between items-start">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-slate-800">{p.ospite_nome || p.fascicolo_id}</p>
                          {carnet && (
                            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">
                              Carnet: {carnet.carnet_residui}/{carnet.carnet_ingressi_totali}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Fascia: {p.fascia} · Stato: {p.stato}
                        </p>
                        {pres?.servizi_inclusi?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {pres.servizi_inclusi.map((s) => (
                              <Badge key={s} variant="secondary" className="text-[10px]">
                                {s}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {pres && (
                          <p className="text-xs text-slate-500 mt-2">
                            Check-in: {pres.check_in ? new Date(pres.check_in).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—"}
                            {pres.tipo_giornata === "mezza" ? " · Mezza giornata (4h)" : pres.check_in ? " · Giornata intera (8h)" : ""}
                            {pres.da_trasporto ? " · Da pulmino" : ""}
                            {haCheckOut && pres.check_out
                              ? ` · Uscita: ${new Date(pres.check_out).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`
                              : ""}
                          </p>
                        )}
                      </div>
                      {canManage && (
                        <div className="flex flex-wrap gap-2 items-center">
                          {!haCheckIn && (
                            <>
                              <Button size="sm" className="bg-[#4CAF50] hover:bg-[#43A047]" onClick={() => handleCheckIn(p.fascicolo_id, "intera")}>
                                <LogIn className="w-4 h-4 mr-1" /> Check-in intera
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleCheckIn(p.fascicolo_id, "mezza")}>
                                Mezza giornata
                              </Button>
                            </>
                          )}
                          {haCheckIn && !haCheckOut && (
                            <Button size="sm" variant="secondary" onClick={() => handleCheckOut(pres.id)}>
                              <LogOut className="w-4 h-4 mr-1" /> Check-out
                            </Button>
                          )}
                          {haCheckIn && haCheckOut && (
                            <Badge variant="outline" className="text-slate-600">
                              Presenza completata
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
